import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { DEFAULT_DASHBOARD_PREFS, DASHBOARD_CARDS_REGISTRY } from "../config/dashboardCards";
import { CONTROLLO_CARDS_REGISTRY } from "../config/controlloCards";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "affidya_dashboard_prefs_v1";

function normalizePanoramicaCard(card, reg) {
  const size = card.size === "compact" ? "compact" : "detailed";
  return { id: card.id, visible: card.visible !== false, size };
}

function normalizeControlloCard(card, reg) {
  const size =
    reg && Array.isArray(reg.supportedSizes) && reg.supportedSizes.includes(card.size)
      ? card.size
      : reg?.defaultSize ?? "list";
  return { id: card.id, visible: card.visible !== false, size };
}

function mergeCardSection(savedList, registry, normalizeFn) {
  const registryIds = registry.map((c) => c.id);
  const merged = [];
  const seenIds = new Set();

  for (const card of savedList) {
    if (!registryIds.includes(card.id)) continue;
    const reg = registry.find((r) => r.id === card.id);
    merged.push(normalizeFn(card, reg));
    seenIds.add(card.id);
  }
  for (const reg of registry) {
    if (!seenIds.has(reg.id)) {
      merged.push({
        id: reg.id,
        visible: reg.defaultVisible,
        size: reg.defaultSize,
      });
    }
  }
  return merged;
}

/** Merge salvataggio/API con registry Panoramica + Controllo completo */
function mergeWithRegistry(saved) {
  const panoramica = mergeCardSection(
    saved.panoramica || [],
    DASHBOARD_CARDS_REGISTRY,
    normalizePanoramicaCard
  );
  const controllo = mergeCardSection(
    saved.controllo || [],
    CONTROLLO_CARDS_REGISTRY,
    normalizeControlloCard
  );
  return {
    panoramica,
    controllo,
    version: saved.version || 1,
  };
}

function readLocalCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.panoramica?.length) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalCache(prefs) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

export function useDashboardPreferences(token) {
  const [prefs, setPrefs] = useState(() => {
    const cached = readLocalCache();
    return cached ? mergeWithRegistry(cached) : DEFAULT_DASHBOARD_PREFS;
  });
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!token || fetchedRef.current) return;
    fetchedRef.current = true;

    axios
      .get(`${API_URL}/api/auth/me/dashboard-prefs`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data || {};
        const merged = mergeWithRegistry({
          panoramica: data.panoramica ?? [],
          controllo: data.controllo ?? [],
          version: data.version ?? 1,
        });
        setPrefs(merged);
        writeLocalCache(merged);
      })
      .catch(() => {
        /* Backend unavailable — keep cache/default */
      })
      .finally(() => setLoading(false));
  }, [token]);

  const save = useCallback(
    async (newPrefs) => {
      setPrefs(newPrefs);
      writeLocalCache(newPrefs);
      if (!token) return;
      await axios.put(`${API_URL}/api/auth/me/dashboard-prefs`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    [token]
  );

  const reset = useCallback(async () => {
    const def = DEFAULT_DASHBOARD_PREFS;
    setPrefs(def);
    writeLocalCache(def);
    if (!token) return;
    await axios.put(`${API_URL}/api/auth/me/dashboard-prefs`, def, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }, [token]);

  return { prefs, setPrefs, save, reset, loading };
}
